import { MarkdownLite as SafeMarkdown } from "@/lib/markdown-lite";
import { useState } from "react";
import { Button } from "@/components/system";
import { Link } from "react-router-dom";
import { PlayCircle } from "lucide-react";
import { allowedVideoEmbedUrl, type TeachingTile } from "@/lib/learn";

export function TeachingTileCard({
  tile,
  showVideo = true,
}: {
  tile: TeachingTile;
  showVideo?: boolean;
}) {
  const embedUrl = showVideo ? allowedVideoEmbedUrl(tile.videoUrl) : null;

  return (
    <article className="ds-card ds-teach">
      {embedUrl ? <TeachingVideo embedUrl={embedUrl} title={tile.title} /> : null}
      <div className="ds-listing__top">
        <span className="ds-eyebrow ds-eyebrow--teal">{tile.specialtyName}</span>
        <time className="ds-fine" dateTime={tile.publishedAt}>{formatTileDate(tile.publishedAt)}</time>
      </div>
      <h3 className="ds-listing__name"><Link to={`/learn/${tile.id}`}>{tile.title}</Link></h3>
      <p className="ds-small">{tileSummary(tile.body)}</p>
      <div className="ds-listing__foot">
        <span className="ds-fine">{[tile.specialist.name, tile.specialist.practice].filter(Boolean).join(" · ")}</span>
        <Link className="ds-listing__go" to={`/learn/${tile.id}`}>
          {embedUrl ? <PlayCircle className="ds-inline-icon" aria-hidden="true" /> : null} Read
        </Link>
      </div>
    </article>
  );
}

export function TeachingVideo({
  embedUrl,
  title,
}: {
  embedUrl: string;
  title: string;
}) {
  const [loadedUrl, setLoadedUrl] = useState<string | null>(null);
  const provider = embedUrl.includes("youtube.com") ? "YouTube" : "Vimeo";
  const watchUrl =
    provider === "YouTube"
      ? embedUrl.replace("/embed/", "/watch?v=")
      : embedUrl.replace("player.vimeo.com/video/", "vimeo.com/");
  return (
    <>
      {loadedUrl === embedUrl ? (
        <div className="pb-learn-video">
          <iframe
            src={embedUrl}
            sandbox="allow-scripts allow-same-origin allow-presentation"
            loading="lazy"
            title={`Video: ${title}`}
            referrerPolicy="strict-origin-when-cross-origin"
            allow="fullscreen; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : (
        <div className="ds-video-consent">
          <PlayCircle aria-hidden="true" />
          <strong>{title}</strong>
          <Button variant="secondary" size="sm" onClick={() => setLoadedUrl(embedUrl)}>
            Load {provider} player
          </Button>
          <p className="ds-fine">
            Loading connects to {provider}, which may collect viewing information. Use the player's CC control for
            captions when available.
          </p>
        </div>
      )}
      <a
        className="ds-link ds-fine"
        href={watchUrl}
        target="_blank"
        rel="noopener noreferrer"
        referrerPolicy="no-referrer"
      >
        Player unavailable? Open on {provider}
      </a>
    </>
  );
}

export function MarkdownLite({ body }: { body: string }) {
  return (
    <SafeMarkdown className="ds-prose">
      {body.replace(/^\*\s+/gm, "- ")}
    </SafeMarkdown>
  );
}

export function formatTileDate(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("en-AU", {
        day: "numeric",
        month: "short",
        year: "numeric",
      }).format(date);
}

function tileSummary(body: string): string {
  const text = body
    .replace(/^[-*]\s+/gm, "")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > 220 ? `${text.slice(0, 217).trimEnd()}…` : text;
}
